import { getRequestAuthPayload } from '@helpers/getRequestAuth';
import type { GetEvalRunResponse, GetEvalRunsResponse } from '@responses/evals';
import type { EvalBudgetStatus } from '@root/domain/schemas/evals/eval-budget.schema';
import axios from 'axios';

/**
 * PBQA eval-run network layer — Contract Review Bot evaluation period.
 *
 * Kept deliberately narrow: we list runs for a submission and fetch the
 * full detail (run header + steps + findings) for the selected run. The
 * feature module polls these from the drawer. This file, together with
 * src/mutations/evalRuns.ts, can be removed when the eval period wraps
 * up.
 */

// Short names for the feature module; the response types are the source of truth.
export type EvalRunSummary = GetEvalRunsResponse[number];
export type EvalRunDetail = GetEvalRunResponse;
export type EvalFinding = GetEvalRunResponse['findings'][number];
export type EvalResolutionCheck = GetEvalRunResponse['resolution_checks'][number];

export const fetchEvalRuns = async (submissionId: string): Promise<GetEvalRunsResponse> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: `/submissions/${submissionId}/eval_runs`,
  });
  const response = await axios(networkPayload);
  return response.data;
};

export type { EvalBudgetStatus };

/**
 * The caller's daily token budget (tenant-wide + their own cap). Rides on
 * the collection route with `?view=budget`; the drawer fetches it on open so
 * "Run evaluation" can be disabled instead of the click hitting a 429.
 */
export const fetchEvalBudget = async (submissionId: string): Promise<EvalBudgetStatus> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: `/submissions/${submissionId}/eval_runs?view=budget`,
  });
  const response = await axios(networkPayload);
  return response.data;
};

export const fetchEvalRun = async (submissionId: string, runId: string): Promise<GetEvalRunResponse> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: `/submissions/${submissionId}/eval_runs/${runId}`,
  });
  const response = await axios(networkPayload);
  return response.data;
};
